import { VStack, Text } from '@chakra-ui/react';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Organization, typeguards } from '@finley-technologies/finley-types';

import { useGetUserQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';

export type ValleyBankUserRoleTypePermissions = {
  advanceRateRead: boolean;
  advanceRateCommit: boolean;
  advanceRateSave: boolean;
  assignmentQueueRead: boolean;
  assignmentQueueEdit: boolean;
  assignmentQueueSubmit: boolean;
};

export type UserRoleTypePermissionsContextType = {
  permissions: ValleyBankUserRoleTypePermissions;
};

// cannot view advance rate or assignment
const DEFAULT_PERMISSIONS: ValleyBankUserRoleTypePermissions = {
  advanceRateRead: false,
  advanceRateSave: false,
  advanceRateCommit: false,
  assignmentQueueRead: false,
  assignmentQueueEdit: false,
  assignmentQueueSubmit: false,
};

export const UserRoleTypePermissionsContext = createContext<UserRoleTypePermissionsContextType>(
  {
    permissions: DEFAULT_PERMISSIONS,
  },
);

/**
 * Actions
 *
 * ADVANCE RATE
 * - advanceRateRead (read advance rate)
 * - advanceRateCommit (commit advance rate changes)
 * - advanceRateSave (save advance rate changes as wip)
 *
 * ASSIGNMENT
 * - assignmentQueueRead (read the queue)
 * - assignmentQueueEdit (add or delete to queue)
 * - assignmentQueueSubmit (submit the final queue to pencil in at end of day)
 */
export const getUserRoleTypeActionPermissions = (
  roleType: Organization.UserRoleType | null,
) => {
  switch (roleType) {
    case 'ADMIN':
      // Can edit assignment + advance rate and force commit changes
      return {
        advanceRateRead: true,
        advanceRateSave: true,
        advanceRateCommit: true,
        assignmentQueueRead: true,
        assignmentQueueEdit: true,
        assignmentQueueSubmit: true,
      };
    case 'MEMBER':
      // Can edit advance rate + save the WIP, can queue assignments
      return {
        advanceRateRead: true,
        advanceRateSave: true,
        advanceRateCommit: false,
        assignmentQueueRead: true,
        assignmentQueueEdit: true,
        assignmentQueueSubmit: false,
      };
    case 'CONTRIBUTOR':
    case null:
      return DEFAULT_PERMISSIONS;
    default:
      return finleyAssertUnreachable(roleType);
  }
};

export const useUserRoleTypePermissionsContext = () => {
  const context = useContext(UserRoleTypePermissionsContext);
  if (context === null) {
    throw new Error('UserRoleTypePermissionsContext must be present');
  }

  return context;
};

type UserRoleTypePremissionsProviderProps = { children: React.ReactNode };
export const UserRoleTypePremissionsProvider = ({
  children,
}: UserRoleTypePremissionsProviderProps) => {
  const [roleType, setRoleType] = useState<Organization.UserRoleType | null>(
    null,
  );

  const { error: isError } = useGetUserQuery({
    onCompleted: (res) => {
      if (
        res.user &&
        typeguards.organization.isUserRoleType(res?.user?.roleType)
      ) {
        setRoleType(res.user.roleType);
      }
    },
  });

  const context: UserRoleTypePermissionsContextType = useMemo(() => {
    return {
      permissions: getUserRoleTypeActionPermissions(roleType),
    };
  }, [roleType]);

  if (isError) {
    return (
      // no header because header will also fail if user doesn't property load
      <VStack w="100%" h="100%">
        <Text>
          There was an error loading your user account. Contact your Finley
          Customer Service Manager for support.
        </Text>
      </VStack>
    );
  }

  return (
    <UserRoleTypePermissionsContext.Provider value={context}>
      {children}
    </UserRoleTypePermissionsContext.Provider>
  );
};
